import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router'
import { ListGroup } from 'react-bootstrap'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import { getReminderLog } from '@severed-links/common.redherrings-reducers/manageUsers'
import * as s from './ReminderLog.scss'
import { capitalCase } from 'change-case'
import { startsWith } from 'lodash'
import moment from 'moment-timezone'
import { FRIENDLY_SHORT_DATE_FORMAT, FRIENDLY_SHORT_TIME_FORMAT } from '@severed-links/common.redherrings-constants'
import PlayerTeamListItem from '../../Football/PlayerTeamListItem'
import SeasonSelector from '../../Football/SeasonSelector'
import WeekSelector from '../../Football/WeekSelector'
import PlayerListItem from '../../Player/PlayerListItem'
import ResetAllEliminatorReminders from '../../Eliminator/Admin/ResetAllEliminatorReminders'

const ReminderDetail = ({ dateSent = null, sentTextReminder = false, sentEmailReminder = false, sentPushReminder = false }) => (
    <div className={s.rightDetail}>
        <div className={s.channels}>
            {sentEmailReminder ?
            <span className={s.channelChip} title='Email reminder sent'>
                <FontAwesomeIcon className={s.icon} name='envelope' />
                <span>Email</span>
            </span>
            : null}
            {sentTextReminder ?
            <span className={s.channelChip} title='Text reminder sent'>
                <FontAwesomeIcon className={s.icon} name='sms' />
                <span>Text</span>
            </span>
            : null}
            {sentPushReminder ?
            <span className={s.channelChip} title='Push reminder sent'>
                <FontAwesomeIcon className={s.icon} name='mobile' />
                <span>Push</span>
            </span>
            : null}
        </div>
        {dateSent && moment(dateSent).isValid() ?
        <div className={s.dateSent}>
            <div>{moment(dateSent).format(FRIENDLY_SHORT_DATE_FORMAT)}</div>
            <div>{moment(dateSent).format(FRIENDLY_SHORT_TIME_FORMAT)}</div>
        </div>
        : null}
    </div>
)

const ReminderLog = () => {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()
    const location = useLocation()
    const app = params.appName || null
    const seasons = useSelector(state => (state[`${(app || '').toLowerCase() || 'football'}`] || {}).seasons || [])
    const reminderLog = useSelector(state => state.manageUsers.reminderLog)
    const seasonName = params.seasonName || null
    const seasonMode = capitalCase(params.seasonMode || '')
    const week = parseInt(params.week || '0')
    const season = seasons.find(i => i.seasonName === seasonName) || {}
    const seasonId = season.seasonId || null
    const reminders = reminderLog && reminderLog.reminders ? reminderLog.reminders : []

    useEffect(() => { getReminders() }, [app, seasonId, seasonMode, week])

    const getReminders = () => {
        if (app && seasonMode && seasonId && week) {
            dispatch(getReminderLog(app, seasonId, seasonMode, week))
        }
    }

    const handleWeekSelect = e => {
        const _isPlayoffWeek = startsWith(e, 'PW')
        const _newWeek = e.replace(/\D+/gi, '')
        const _newPath = location.pathname
            .replace(`/${seasonMode.toLowerCase()}/`, `/${_isPlayoffWeek ? 'postseason' : 'regular'}/`)
            .replace(/\/week\/\d+$/gi, `/week/${_newWeek}`)
        navigate(_newPath)
    }

    return (
        <div className={s.container + (app === 'eliminator' ? ' ' + s.contest : '')}>
            <section className={s.pageHeader}>
                <button className={s.goBack} onClick={() => navigate(-1)}>
                    <FontAwesomeIcon name='chevron-left' />
                </button>
                <div className={s.headerCopy}>
                    <div className={s.eyebrow}>{capitalCase(app)} Admin</div>
                    <h2 className={s.header}>Reminder Log</h2>
                    <div className={s.subtitle}>{seasonName} {seasonMode} {seasonMode === 'Regular' ? 'Season' : ''} Week {week}</div>
                </div>
                <div className={s.headerActions}>
                    {(app || '').toLowerCase() === 'eliminator' ?
                    <ResetAllEliminatorReminders seasonId={seasonId} />
                    : null}
                    <SeasonSelector seasons={seasons} season={season} />
                    <WeekSelector seasons={seasons} season={season}
                            week={`${(seasonMode === 'Preseason' ? 'Pre' : seasonMode === 'Postseason' ? 'PW' : 'W')}${week}`}
                            showPlayoffWeeks={seasonMode === 'Postseason'}
                            showPreseasonWeeks={seasonMode === 'Preseason'}
                            showRegularSeasonWeeks={seasonMode === 'Regular'}
                            onSelect={e => handleWeekSelect(e)} />
                </div>
            </section>

            <section className={s.logSummary}>
                <div className={s.summaryPill}>
                    <span>{reminders.length}</span>
                    reminders sent
                </div>
                <div className={s.summaryPill}>
                    <span>{reminders.filter(i => i.sentEmailReminder).length}</span>
                    email
                </div>
                <div className={s.summaryPill}>
                    <span>{reminders.filter(i => i.sentTextReminder).length}</span>
                    text
                </div>
                <div className={s.summaryPill}>
                    <span>{reminders.filter(i => i.sentPushReminder).length}</span>
                    push
                </div>
            </section>

            <section className={s.reminderCard}>
                <div className={s.listHeader}>
                    <div>Recipient</div>
                    <div>Delivery</div>
                </div>
                <ListGroup className={s.reminderList}>
            {reminders.map(i =>
                i.hasTeam ?
                <PlayerTeamListItem {...i}
                    className={s.reminderRow}
                    detail={i.playerName}
                    right={<ReminderDetail {...i} />}
                    key={`reminder-log-${i._id}`} />
                :
                <PlayerListItem player={i}
                    className={s.reminderRow}
                    rightDetail={<ReminderDetail {...i} />}
                    key={`reminder-log-${i._id}`} />
            )}
            {!reminders.length ? <ListGroup.Item className={s.emptyState}>No reminders located</ListGroup.Item> : null}
                </ListGroup>
            </section>
        </div>
    )
}

export default ReminderLog
